import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiDownload, FiStar, FiClock, FiCopy, FiCheck, FiChevronRight, FiGithub, FiExternalLink } from 'react-icons/fi';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import toast from 'react-hot-toast';
import api from '../utils/api';
import AgentBadge from '../components/AgentBadge';
import { scoreSkill, type ScoreReport } from '../lib/skillScorer';

interface SkillDetail {
  _id: string;
  name: string;
  slug?: string;
  owner?: string;
  repoOwner?: string;
  repoUrl?: string;
  description: string;
  longDescription?: string;
  skillMdContent?: string;
  categories?: string[];
  tags?: string[];
  compatibleAgents?: string[];
  installCount?: number;
  stars?: number;
  starCount?: number;
  githubUrl?: string;
  lastUpdated?: string;
  updatedAt?: string;
  authorUsername?: string;
  authorAvatar?: string;
  authorBio?: string;
  authorId?: { username?: string; displayName?: string; avatarUrl?: string; bio?: string };
  version?: string;
  securityScan?: {
    score: number;
    severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
    recommendation?: string;
    findings?: Array<{
      id?: string;
      title?: string;
      severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
      category?: string;
      location?: string | { file?: string; start_line?: number; end_line?: number | null };
      confidence?: number;
      explanation?: string;
    }>;
    componentCount?: number;
    scannedAt?: string;
  };
  qualityScore?: number;
  qualityGrade?: string;
}

function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return n.toString();
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function CopyButton({ text, label }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Copied to clipboard!');
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };
  return (
    <button
      onClick={handleCopy}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-slate-400 hover:text-brand-400 hover:bg-white/5 transition-all"
      title="Copy to clipboard"
    >
      {copied ? <FiCheck size={12} className="text-green-400" /> : <FiCopy size={12} />}
      {label || 'Copy'}
    </button>
  );
}

function InstallCommand({ owner, name, agent }: { owner: string; name: string; agent: string }) {
  const cmd = `npx skillsai add ${owner}/${name} --agent ${agent}`;
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(cmd);
      setCopied(true);
      toast.success('Copied to clipboard!');
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  return (
    <button
      onClick={handleCopy}
      className="flex items-center justify-between w-full p-3 bg-black rounded-xl border border-white/5 hover:border-brand-500/30 transition-all cursor-pointer text-left group"
      title="Click to copy"
    >
      <code className="text-sm text-brand-400 font-mono truncate mr-3">
        <span className="text-slate-500 select-none mr-2">$</span>
        {cmd}
      </code>
      <span className="flex items-center gap-1.5 text-xs font-medium text-slate-500 group-hover:text-brand-400 transition-colors shrink-0">
        {copied ? <FiCheck size={12} className="text-green-400" /> : <FiCopy size={12} />}
        {copied ? 'Copied!' : 'Copy'}
      </span>
    </button>
  );
}

const PLACEHOLDER_SKILL: SkillDetail = {
  _id: 'demo',
  name: 'code-review',
  owner: 'strongly-ai',
  description: 'Comprehensive code review with security analysis, performance optimization, and best practices enforcement.',
  longDescription: 'A thorough code review skill that analyzes your code for security vulnerabilities, performance bottlenecks, and adherence to best practices. It provides actionable suggestions with code examples.',
  skillMdContent: `# Code Review Skill

## Description
A comprehensive code review skill that analyzes code for quality, security, and performance.

## Features
- **Security Analysis**: Detects common vulnerabilities (SQL injection, XSS, CSRF)
- **Performance Review**: Identifies N+1 queries, memory leaks, and bottlenecks
- **Best Practices**: Enforces coding standards and design patterns
- **Accessibility**: Checks for a11y compliance in frontend code

## Usage
\`\`\`bash
# Install the skill
npx skillsai add strongly-ai/code-review --agent claude-code
\`\`\`

Once installed, the skill will be available in your AI agent's context. Simply ask your agent to review code:

\`\`\`
Review this file for security issues and performance improvements.
\`\`\`

## Configuration
You can customize the review focus in your \`.aiskills.json\`:

\`\`\`json
{
  "skills": {
    "strongly-ai/code-review": {
      "focus": ["security", "performance", "accessibility"],
      "severity": "strict"
    }
  }
}
\`\`\`

## Examples

### Security Review
The skill will flag issues like:
- Unsanitized user input
- Hardcoded credentials
- Insecure HTTP calls
- Missing CORS configuration

### Performance Review
The skill identifies:
- Unnecessary re-renders in React
- N+1 database queries
- Large bundle sizes
- Missing memoization
`,
  categories: ['code-quality', 'security', 'performance'],
  tags: ['review', 'lint', 'security', 'best-practices'],
  compatibleAgents: ['claude-code', 'cursor', 'windsurf', 'codex', 'copilot', 'aider', 'cline', 'roo-code', 'continue', 'devin'],
  installCount: 12450,
  stars: 342,
  githubUrl: 'https://github.com/strongly-ai/code-review-skill',
  lastUpdated: '2026-03-10T12:00:00Z',
  authorUsername: 'strongly-ai',
  authorBio: 'Building the AI skills ecosystem.',
  version: '1.2.0',
};

export default function Skill() {
  const { owner, skill: skillName } = useParams<{ owner: string; skill: string }>();
  const [skill, setSkill] = useState<SkillDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchSkill() {
      setLoading(true);
      try {
        const response = await api.get(`/skills/${owner}/${skillName}`);
        setSkill(response.data.skill || response.data);
      } catch (err: any) {
        // Only use placeholder data in development; in production show null (not found)
        if (import.meta.env.DEV && err.response?.status !== 404) {
          setSkill(PLACEHOLDER_SKILL);
        } else {
          setSkill(null);
        }
      } finally {
        setLoading(false);
      }
    }
    fetchSkill();
  }, [owner, skillName]);

  if (loading) {
    return (
      <div className="pt-24 pb-16 max-w-6xl mx-auto px-4">
        <div className="space-y-6">
          <div className="h-10 w-64 shimmer rounded-xl" />
          <div className="h-6 w-96 shimmer rounded-lg" />
          <div className="h-40 shimmer rounded-2xl" />
          <div className="h-96 shimmer rounded-2xl" />
        </div>
      </div>
    );
  }

  if (!skill) {
    return (
      <div className="pt-24 pb-16 max-w-6xl mx-auto px-4 text-center">
        <h2 className="text-2xl font-bold text-white mb-4">Skill Not Found</h2>
        <p className="text-slate-400 mb-6">
          The skill {owner}/{skillName} could not be found.
        </p>
        <Link to="/browse" className="btn-gradient text-sm px-6 py-3 rounded-xl inline-block">
          <span>Browse Skills</span>
        </Link>
      </div>
    );
  }

  // Simple markdown to HTML (basic parser for display)
  const renderMarkdown = (md: string) => {
    const lines = md.split('\n');
    const elements: React.ReactNode[] = [];
    let inCodeBlock = false;
    let codeBlockContent = '';
    let codeBlockLang = '';
    let key = 0;

    // Sanitize URLs to prevent javascript: and data: protocol XSS
    const sanitizeUrl = (url: string): string => {
      const trimmed = url.trim().toLowerCase();
      if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('/') || trimmed.startsWith('#')) {
        return url;
      }
      return '#';
    };

    const processInline = (text: string): React.ReactNode => {
      // Process inline markdown: images, links, bold, inline code, italic
      // Split on patterns and reconstruct
      const tokens: React.ReactNode[] = [];
      let remaining = text;
      let tokenKey = 0;

      while (remaining.length > 0) {
        // Image: ![alt](url)
        const imgMatch = remaining.match(/^!\[([^\]]*)\]\(([^)]+)\)/);
        if (imgMatch) {
          tokens.push(<img key={tokenKey++} src={sanitizeUrl(imgMatch[2])} alt={imgMatch[1]} className="inline max-h-64 rounded-lg" />);
          remaining = remaining.slice(imgMatch[0].length);
          continue;
        }

        // Link: [text](url)
        const linkMatch = remaining.match(/^\[([^\]]+)\]\(([^)]+)\)/);
        if (linkMatch) {
          tokens.push(
            <a key={tokenKey++} href={sanitizeUrl(linkMatch[2])} target="_blank" rel="noopener noreferrer" className="text-brand-400 hover:text-brand-300 underline underline-offset-2 transition-colors">
              {linkMatch[1]}
            </a>
          );
          remaining = remaining.slice(linkMatch[0].length);
          continue;
        }

        // Inline code: `code`
        const codeMatch = remaining.match(/^`([^`]+)`/);
        if (codeMatch) {
          tokens.push(
            <code key={tokenKey++} className="px-1.5 py-0.5 rounded bg-white/10 text-brand-300 text-xs font-mono">
              {codeMatch[1]}
            </code>
          );
          remaining = remaining.slice(codeMatch[0].length);
          continue;
        }

        // Bold: **text**
        const boldMatch = remaining.match(/^\*\*(.+?)\*\*/);
        if (boldMatch) {
          tokens.push(<strong key={tokenKey++} className="text-white font-semibold">{boldMatch[1]}</strong>);
          remaining = remaining.slice(boldMatch[0].length);
          continue;
        }

        // Italic: *text*
        const italicMatch = remaining.match(/^\*(.+?)\*/);
        if (italicMatch) {
          tokens.push(<em key={tokenKey++} className="italic text-slate-200">{italicMatch[1]}</em>);
          remaining = remaining.slice(italicMatch[0].length);
          continue;
        }

        // Plain text: consume up to the next special character
        const nextSpecial = remaining.slice(1).search(/[!\[`*]/);
        if (nextSpecial === -1) {
          tokens.push(remaining);
          break;
        } else {
          tokens.push(remaining.slice(0, nextSpecial + 1));
          remaining = remaining.slice(nextSpecial + 1);
        }
      }

      return tokens.length === 1 ? tokens[0] : tokens;
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      if (line.startsWith('```')) {
        if (inCodeBlock) {
          elements.push(
            <div key={key++} className="mb-4 rounded-xl overflow-hidden border border-white/10">
              <SyntaxHighlighter
                language={codeBlockLang || 'text'}
                style={vscDarkPlus}
                customStyle={{
                  margin: 0,
                  padding: '16px 20px',
                  background: '#000',
                  fontSize: '13px',
                  borderRadius: '12px',
                }}
              >
                {codeBlockContent.trim()}
              </SyntaxHighlighter>
            </div>
          );
          codeBlockContent = '';
          codeBlockLang = '';
          inCodeBlock = false;
        } else {
          inCodeBlock = true;
          codeBlockLang = line.replace('```', '').trim();
        }
        continue;
      }

      if (inCodeBlock) {
        codeBlockContent += line + '\n';
        continue;
      }

      if (line.startsWith('# ')) {
        elements.push(<h1 key={key++} className="text-3xl font-bold text-white mt-8 mb-4">{processInline(line.slice(2))}</h1>);
      } else if (line.startsWith('## ')) {
        elements.push(<h2 key={key++} className="text-2xl font-bold text-white mt-8 mb-3">{processInline(line.slice(3))}</h2>);
      } else if (line.startsWith('### ')) {
        elements.push(<h3 key={key++} className="text-xl font-semibold text-white mt-6 mb-2">{processInline(line.slice(4))}</h3>);
      } else if (line.startsWith('- ')) {
        elements.push(
          <div key={key++} className="flex gap-2 ml-4 mb-1.5 text-slate-300 text-sm">
            <span className="text-brand-400 mt-0.5 shrink-0">&bull;</span>
            <span>{processInline(line.slice(2))}</span>
          </div>
        );
      } else if (/^\d+\.\s/.test(line)) {
        const match = line.match(/^(\d+)\.\s(.*)$/);
        if (match) {
          elements.push(
            <div key={key++} className="flex gap-2 ml-4 mb-1.5 text-slate-300 text-sm">
              <span className="text-brand-400 mt-0.5 shrink-0 font-mono text-xs w-5 text-right">{match[1]}.</span>
              <span>{processInline(match[2])}</span>
            </div>
          );
        }
      } else if (line.trim() === '') {
        elements.push(<div key={key++} className="h-2" />);
      } else {
        elements.push(
          <p key={key++} className="text-slate-300 text-sm leading-relaxed mb-2">
            {processInline(line)}
          </p>
        );
      }
    }

    return elements;
  };

  // Derive owner and author from multiple possible sources
  const skillOwner = skill.owner || skill.repoOwner || skill.slug?.split('/')[0] || owner || 'unknown';
  const authorName = skill.authorUsername || skill.authorId?.username || skillOwner;
  const authorBio = skill.authorBio || skill.authorId?.bio;
  const githubUrl = skill.githubUrl || skill.repoUrl;
  const lastUpdated = skill.lastUpdated || skill.updatedAt;
  const starCount = skill.stars ?? skill.starCount ?? 0;

  const DEFAULT_AGENTS = ['claude-code', 'cursor', 'windsurf', 'copilot'];
  const topAgents = (skill.compatibleAgents?.length ? skill.compatibleAgents : DEFAULT_AGENTS).slice(0, 6);

  return (
    <div className="pt-24 pb-16">
      <div className="max-w-6xl mx-auto px-4">
        {/* Breadcrumb */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-2 text-sm text-slate-500 mb-8"
        >
          <Link to="/browse" className="hover:text-brand-400 transition-colors">Skills</Link>
          <FiChevronRight size={12} />
          <Link to={`/u/${skillOwner}`} className="hover:text-brand-400 transition-colors">{skillOwner}</Link>
          <FiChevronRight size={12} />
          <span className="text-white">{skill.name}</span>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Title Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-500 to-brand-purple-500 flex items-center justify-center text-white text-xl font-bold shrink-0">
                  {skill.name[0].toUpperCase()}
                </div>
                <div className="min-w-0">
                  <h1 className="text-3xl sm:text-4xl font-bold text-white mb-1">{skill.name}</h1>
                  <p className="text-slate-400 text-sm">{skillOwner}/{skill.name}</p>
                </div>
              </div>
              <p className="text-lg text-slate-300 leading-relaxed">{skill.description}</p>
            </motion.div>

            {/* Stats Row */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.5 }}
              className="flex flex-wrap gap-6 py-4 border-y border-white/5"
            >
              <div className="flex items-center gap-2 text-sm">
                <FiDownload size={16} className="text-brand-400" />
                <span className="text-white font-semibold">{formatCount(skill.installCount || 0)}</span>
                <span className="text-slate-500">installs</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <FiStar size={16} className="text-yellow-500" />
                <span className="text-white font-semibold">{formatCount(starCount)}</span>
                <span className="text-slate-500">stars</span>
              </div>
              {lastUpdated && (
                <div className="flex items-center gap-2 text-sm">
                  <FiClock size={16} className="text-slate-500" />
                  <span className="text-slate-400">Updated {formatDate(lastUpdated)}</span>
                </div>
              )}
              {skill.version && (
                <div className="text-sm">
                  <span className="px-2 py-0.5 rounded-md bg-white/5 text-slate-400 font-mono text-xs">
                    v{skill.version}
                  </span>
                </div>
              )}
            </motion.div>

            {/* Install Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="glass-card p-6"
            >
              <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <FiDownload className="text-brand-400" />
                Install
              </h2>
              <div className="space-y-3">
                {topAgents.map((agent) => (
                  <InstallCommand
                    key={agent}
                    owner={skillOwner}
                    name={skill.name}
                    agent={agent}
                  />
                ))}
                {(skill.compatibleAgents?.length || 0) > 6 && (
                  <p className="text-xs text-slate-500 pt-1">
                    + {(skill.compatibleAgents?.length || 0) - 6} more compatible agents
                  </p>
                )}
              </div>
            </motion.div>

            {/* SKILL.md Content */}
            {skill.skillMdContent && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.5 }}
                className="glass-card p-8"
              >
                <div className="markdown-content">
                  {renderMarkdown(skill.skillMdContent)}
                </div>
              </motion.div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Author Card */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="glass-card p-6"
            >
              <h3 className="text-sm font-medium text-slate-400 mb-4 uppercase tracking-wide">Author</h3>
              <Link
                to={`/u/${skillOwner}`}
                className="flex items-center gap-3 group"
              >
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-brand-500 to-brand-purple-500 flex items-center justify-center text-white font-bold text-sm">
                  {authorName[0].toUpperCase()}
                </div>
                <div>
                  <div className="text-sm font-semibold text-white group-hover:text-brand-400 transition-colors">
                    {authorName}
                  </div>
                  {authorBio && (
                    <div className="text-xs text-slate-500 line-clamp-1">{authorBio}</div>
                  )}
                </div>
              </Link>
            </motion.div>

            {/* Compatible Agents */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="glass-card p-6"
            >
              <h3 className="text-sm font-medium text-slate-400 mb-4 uppercase tracking-wide">Compatible Agents</h3>
              <div className="flex flex-wrap gap-2">
                {(skill.compatibleAgents?.length ? skill.compatibleAgents : DEFAULT_AGENTS).map((agent) => (
                  <AgentBadge key={agent} agentName={agent} size="md" />
                ))}
              </div>
              {!skill.compatibleAgents?.length && (
                <p className="text-xs text-slate-500 mt-3">Works with most AI coding agents</p>
              )}
            </motion.div>

            {/* Categories */}
            {skill.categories && skill.categories.length > 0 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.35, duration: 0.5 }}
                className="glass-card p-6"
              >
                <h3 className="text-sm font-medium text-slate-400 mb-4 uppercase tracking-wide">Categories</h3>
                <div className="flex flex-wrap gap-2">
                  {skill.categories.map((cat) => (
                    <Link
                      key={cat}
                      to={`/browse?category=${cat}`}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium bg-white/5 text-slate-300 border border-white/5 hover:border-brand-500/30 hover:text-brand-400 transition-all"
                    >
                      {cat}
                    </Link>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Tags */}
            {skill.tags && skill.tags.length > 0 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4, duration: 0.5 }}
                className="glass-card p-6"
              >
                <h3 className="text-sm font-medium text-slate-400 mb-4 uppercase tracking-wide">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {skill.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-1 rounded-md text-xs text-slate-400 bg-white/5 font-mono"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Links */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.45, duration: 0.5 }}
              className="glass-card p-6"
            >
              <h3 className="text-sm font-medium text-slate-400 mb-4 uppercase tracking-wide">Links</h3>
              <div className="space-y-2">
                {githubUrl && (
                  <a
                    href={githubUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-slate-300 hover:text-brand-400 transition-colors py-1"
                  >
                    <FiGithub size={14} />
                    GitHub Repository
                    <FiExternalLink size={11} className="ml-auto" />
                  </a>
                )}
                <a
                  href={`https://www.npmjs.com/package/aiskills`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-slate-300 hover:text-brand-400 transition-colors py-1"
                >
                  <FiDownload size={14} />
                  npm Package
                  <FiExternalLink size={11} className="ml-auto" />
                </a>
              </div>
            </motion.div>

            {/* Quality score (Evaluator) */}
            {skill.skillMdContent && (() => {
              let report: ScoreReport | null = null;
              try { report = scoreSkill({ source: 'paste', skillMd: skill.skillMdContent }); } catch { return null; }
              if (!report) return null;
              const gradeColor =
                report.grade === 'Excellent' ? 'text-emerald-300 bg-emerald-500/10 border-emerald-500/40' :
                report.grade === 'Strong' ? 'text-brand-300 bg-brand-500/10 border-brand-500/40' :
                report.grade === 'Decent' ? 'text-amber-300 bg-amber-500/10 border-amber-500/40' :
                report.grade === 'Needs work' ? 'text-orange-300 bg-orange-500/10 border-orange-500/40' :
                'text-red-300 bg-red-500/10 border-red-500/40';
              const top = [...report.categories].sort((a, b) => (b.weight - b.earned) - (a.weight - a.earned)).slice(0, 3);
              return (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.48, duration: 0.5 }}
                  className="glass-card p-6"
                >
                  <h3 className="text-sm font-medium text-slate-400 mb-4 uppercase tracking-wide">
                    Quality Score
                  </h3>
                  <div className={`flex items-baseline gap-3 p-3 rounded-lg border ${gradeColor} mb-3`}>
                    <div className="text-2xl font-bold tabular-nums">{report.score}</div>
                    <div className="text-xs text-slate-400">/ 100</div>
                    <div className="text-xs font-semibold ml-auto">{report.grade}</div>
                  </div>
                  <div className="space-y-1.5">
                    {top.map((c) => {
                      const pct = c.weight === 0 ? 100 : Math.round((c.earned / c.weight) * 100);
                      return (
                        <div key={c.id}>
                          <div className="flex justify-between text-[11px] mb-0.5">
                            <span className="text-slate-400">{c.label}</span>
                            <span className="text-slate-500 tabular-nums">{c.earned.toFixed(1)} / {c.weight}</span>
                          </div>
                          <div className="h-1 rounded-full bg-white/5 overflow-hidden">
                            <div
                              className={pct >= 90 ? 'bg-emerald-400 h-full' : pct >= 70 ? 'bg-brand-500 h-full' : pct >= 50 ? 'bg-amber-400 h-full' : 'bg-red-400 h-full'}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <Link
                    to={`/evaluate`}
                    className="inline-block mt-3 text-[11px] text-brand-300 hover:text-brand-200"
                  >
                    Open in evaluator →
                  </Link>
                </motion.div>
              );
            })()}

            {/* Security report (sidebar) */}
            {skill.securityScan && (() => {
              const sev = skill.securityScan.severity;
              const ring =
                sev === 'CRITICAL' ? 'text-red-300 bg-red-500/10 border-red-500/40' :
                sev === 'HIGH' ? 'text-orange-300 bg-orange-500/10 border-orange-500/40' :
                sev === 'MEDIUM' ? 'text-amber-300 bg-amber-500/10 border-amber-500/40' :
                'text-emerald-300 bg-emerald-500/10 border-emerald-500/40';
              const findings = skill.securityScan.findings ?? [];
              return (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5, duration: 0.5 }}
                  className="glass-card p-6"
                >
                  <h3 className="text-sm font-medium text-slate-400 mb-4 uppercase tracking-wide">
                    Security Report
                  </h3>
                  <div className={`flex items-baseline gap-3 p-3 rounded-lg border ${ring} mb-3`}>
                    <div className="text-2xl font-bold tabular-nums">{skill.securityScan.score}</div>
                    <div className="text-xs text-slate-400">risk</div>
                    <div className="text-xs font-semibold ml-auto">{sev}</div>
                  </div>
                  {skill.securityScan.recommendation && (
                    <p className="text-xs text-slate-300 mb-3">{skill.securityScan.recommendation}</p>
                  )}
                  <div className="text-xs text-slate-500 mb-2">
                    {findings.length} finding{findings.length === 1 ? '' : 's'}
                  </div>
                  <div className="space-y-1.5">
                    {findings.slice(0, 3).map((f, i) => (
                      <div key={(f.id ?? '') + i} className="border border-white/5 rounded-md p-2 bg-black/20">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{f.severity}</div>
                        <div className="text-xs text-white truncate">{f.title || f.id}</div>
                      </div>
                    ))}
                    {findings.length > 3 && (
                      <p className="text-[11px] text-slate-500 pt-1">+ {findings.length - 3} more</p>
                    )}
                  </div>
                  {skill.securityScan.scannedAt && (
                    <p className="text-[10px] text-slate-600 mt-3">
                      Scanned {formatDate(skill.securityScan.scannedAt)}
                    </p>
                  )}
                </motion.div>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
}
